import { useState } from 'react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Sheet,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  Typography,
} from '@mui/joy';
import { ChatCircleText, ClockCounterClockwise } from '@phosphor-icons/react';
import { AdminDevAPI } from '../../../lib/apiAdmin';
import { MentionTextarea } from '../../../components/admin/MentionTextarea';
import { ConfirmDeleteDialog } from '../../../components/common/ConfirmDeleteDialog';
import { useProfileDraft } from '../organizations/ProfileDrafts';
import type { DevTicketComment } from '../../../types/admin';
import { AttachmentGallery, FilePicker } from './TicketAttachments';
import { addDraftFiles, pastedFiles } from './ticketFiles';
import { TicketError, TicketMarkdown, TicketSection } from './ticketShared';
import { label, when } from './ticketUtils';

function Comment({ row, refresh }: { row: DevTicketComment; refresh: () => void }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(row.body);
  const [deleting, setDeleting] = useState(false);
  const save = useMutation({
    mutationFn: () => AdminDevAPI.updateComment(row.id, body),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => AdminDevAPI.deleteComment(row.id),
    onSuccess: () => {
      setDeleting(false);
      refresh();
    },
  });
  useProfileDraft(`comment-${row.id}`, editing && body !== row.body, save.isPending || remove.isPending);
  return (
    <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0 }}>
      <Avatar size="sm" color="neutral">
        {(row.author_name || '?').slice(0, 1).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
          <Typography level="title-sm">{row.author_name || 'Former team member'}</Typography>
          <Typography level="body-xs">
            {when(row.created_at)}
            {Date.parse(row.updated_at) - Date.parse(row.created_at) > 1000 ? ' · Edited' : ''}
          </Typography>
        </Box>
        {editing ? (
          <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
            <Textarea
              aria-label="Edit comment"
              minRows={3}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              disabled={save.isPending}
            />
            <TicketError error={save.error} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="sm"
                loading={save.isPending}
                disabled={!body.trim() && !row.attachments?.length}
                onClick={() => save.mutate()}
              >
                Save comment
              </Button>
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                disabled={save.isPending}
                onClick={() => {
                  setEditing(false);
                  setBody(row.body);
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <TicketMarkdown text={row.body} />
        )}
        <AttachmentGallery files={row.attachments ?? []} />
        {row.can_edit && !editing && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => {
                setBody(row.body);
                setEditing(true);
              }}
            >
              Edit comment
            </Button>
            <Button size="sm" variant="plain" color="neutral" onClick={() => setDeleting(true)}>
              Delete comment
            </Button>
          </Box>
        )}
      </Box>
      <ConfirmDeleteDialog
        open={deleting}
        label="Delete comment"
        pending={remove.isPending}
        error={remove.error}
        onClose={() => setDeleting(false)}
        onConfirm={() => remove.mutate()}
      >
        <Typography>This removes your comment and any files attached to it.</Typography>
      </ConfirmDeleteDialog>
    </Box>
  );
}
export function TicketDiscussion({ ticketId, refresh }: { ticketId: number; refresh: () => void }) {
  const [tab, setTab] = useState('comments');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<unknown>(null);
  const comments = useInfiniteQuery({
    queryKey: ['admin', 'devTicket', ticketId, 'comments'],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => AdminDevAPI.listComments(ticketId, pageParam),
    getNextPageParam: (page, _pages, previous) => (page.next ? previous + 1 : undefined),
  });
  const activity = useInfiniteQuery({
    queryKey: ['admin', 'devTicket', ticketId, 'activity'],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => AdminDevAPI.ticketActivity(ticketId, pageParam),
    getNextPageParam: (page, _pages, previous) => (page.next ? previous + 1 : undefined),
  });
  const send = useMutation({
    mutationFn: () => AdminDevAPI.addComment(ticketId, body, files),
    onSuccess: () => {
      setBody('');
      setFiles([]);
      setFileError(null);
      refresh();
    },
  });
  useProfileDraft('new-comment', !!body.trim() || files.length > 0, send.isPending);
  const submit = () => {
    if ((body.trim() || files.length) && !send.isPending) send.mutate();
  };
  return (
    <TicketSection title="Discussion & activity" icon={<ChatCircleText size={20} />}>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(String(value))}
        sx={{ bgcolor: 'transparent', minWidth: 0 }}
      >
        <TabList size="sm" variant="plain" sx={{ mb: 2 }}>
          <Tab value="comments">Comments {comments.data ? `(${comments.data.pages[0].count})` : ''}</Tab>
          <Tab value="history">Change history</Tab>
        </TabList>
        <TabPanel value="comments" keepMounted sx={{ p: 0, minWidth: 0 }}>
          <Sheet
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 'md', mb: 3 }}
            onPaste={(event) => {
              const incoming = pastedFiles(event);
              if (incoming.length && !send.isPending) {
                try {
                  setFiles(addDraftFiles(files, incoming));
                  setFileError(null);
                } catch (problem) {
                  setFileError(problem);
                }
              }
            }}
            onKeyDown={(event) => {
              if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.preventDefault();
                submit();
              }
            }}
          >
            <MentionTextarea
              value={body}
              onChange={setBody}
              minRows={3}
              disabled={send.isPending}
              placeholder="Share an update… Use @name to mention a teammate."
            />
            <FilePicker
              files={files}
              onChange={setFiles}
              disabled={send.isPending}
              label="Attach to comment"
            />
            <TicketError error={fileError || send.error} />
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, gap: 1 }}
            >
              <Typography level="body-xs">Internal discussion · Ctrl/⌘ + Enter to post</Typography>
              <Button
                size="sm"
                disabled={!body.trim() && !files.length}
                loading={send.isPending}
                onClick={submit}
              >
                Post comment
              </Button>
            </Box>
          </Sheet>
          <TicketError error={comments.error} retry={() => comments.refetch()} />
          {comments.isLoading && <Typography role="status">Loading comments…</Typography>}
          {comments.data?.pages[0].count === 0 && (
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              No comments yet. Start with what you found or what needs to happen next.
            </Typography>
          )}
          <Box sx={{ display: 'grid', gap: 2.5 }}>
            {comments.data?.pages
              .flatMap((page) => page.results)
              .map((row) => (
                <Comment key={row.id} row={row} refresh={refresh} />
              ))}
          </Box>
          {comments.hasNextPage && (
            <Button
              sx={{ mt: 2 }}
              size="sm"
              variant="outlined"
              loading={comments.isFetchingNextPage}
              onClick={() => comments.fetchNextPage()}
            >
              Load older comments
            </Button>
          )}
        </TabPanel>
        <TabPanel value="history" keepMounted sx={{ p: 0, minWidth: 0 }}>
          <Typography level="body-sm" sx={{ mb: 2 }}>
            Who changed what, and when. Ticket updates and file changes are recorded automatically.
          </Typography>
          <TicketError error={activity.error} retry={() => activity.refetch()} />
          {activity.isLoading && <Typography role="status">Loading activity…</Typography>}
          {activity.data?.pages[0].count === 0 && (
            <Typography level="body-sm">
              No recorded updates. Older edits may not have been captured.
            </Typography>
          )}
          {activity.data?.pages
            .flatMap((page) => page.results)
            .map((entry) => (
              <Box key={entry.id} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                <ClockCounterClockwise size={18} style={{ flexShrink: 0, marginTop: 3 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography level="body-sm">
                    <strong>{entry.actor_name}</strong> · {entry.summary}
                  </Typography>
                  <Typography level="body-xs">{when(entry.created_at)}</Typography>
                  {Object.keys(entry.changes).length > 0 && (
                    <Box
                      component="details"
                      sx={{
                        mt: 0.75,
                        fontSize: 'sm',
                        overflowWrap: 'anywhere',
                        '& summary': { cursor: 'pointer', color: 'primary.plainColor' },
                      }}
                    >
                      <summary>View changes</summary>
                      {Object.entries(entry.changes).map(([field, change]) => (
                        <Box
                          key={field}
                          sx={{ bgcolor: 'background.level1', p: 1.25, borderRadius: 'sm', mt: 0.75 }}
                        >
                          <Typography level="title-sm">{label(field)}</Typography>
                          <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                            Before: {change.before || 'Empty'}
                          </Typography>
                          <Divider sx={{ my: 0.5 }} />
                          <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                            After: {change.after || 'Empty'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          {activity.hasNextPage && (
            <Button
              size="sm"
              variant="outlined"
              loading={activity.isFetchingNextPage}
              onClick={() => activity.fetchNextPage()}
            >
              Load older activity
            </Button>
          )}
        </TabPanel>
      </Tabs>
    </TicketSection>
  );
}
